import type { CatchReward, EventReward, OfferItemKind, OrderReward, WyrmReward } from "@highmarch/domain";

/**
 * Where every kind of reward lives on screen (owner 2026-10-24: "gems into the top right section, gold into the shop,
 * items into armory, etc"). Tables, not ifs: a kind → its home, a home → the places on screen that show it (most
 * specific first), and each grant shape the game pays (offer items, event rewards, catches, the Wyrm, orders) → the
 * kinds it can pay, so a test can prove nothing grantable is homeless (test/rewardHomes.test.ts).
 */

/** Everything a grant can add to what the player holds (holdings.ts reads each one from the save). */
export const REWARD_KINDS = [
  "gems", "gold", "marks", "trophies", "essence", "sigil", "gear", "speedUp", "supply", "pet", "treat", "skin", "emblem", "embers", "builder", "vipXp",
] as const;
export type RewardKind = (typeof REWARD_KINDS)[number];

/** The places a reward flies home to. */
export const HOME_IDS = ["gems", "gold", "marks", "trophies", "armoury", "skins", "supplies", "towers", "pets", "events", "vip", "hub"] as const;
export type HomeId = (typeof HOME_IDS)[number];

/** Each kind's home and whether it is a unique item (one icon per item) or a counted amount (a log-sized burst). */
export const REWARD_HOMES: Readonly<Record<RewardKind, { readonly home: HomeId; readonly unique: boolean }>> = {
  gems: { home: "gems", unique: false },
  gold: { home: "gold", unique: false },
  marks: { home: "marks", unique: false },
  trophies: { home: "trophies", unique: false },
  // gear v2's forge (Essence, Fusion Sigils) lives in the Armoury with the gear it raises
  essence: { home: "armoury", unique: false },
  sigil: { home: "armoury", unique: false },
  gear: { home: "armoury", unique: true },
  // speed-ups and builders serve the upgrade queue, on the Towers tab
  speedUp: { home: "towers", unique: false },
  builder: { home: "towers", unique: true },
  supply: { home: "supplies", unique: false },
  pet: { home: "pets", unique: true },
  treat: { home: "pets", unique: false },
  skin: { home: "skins", unique: true },
  // a flag emblem is a cosmetic, applied from the Skins package like the skins
  emblem: { home: "skins", unique: true },
  embers: { home: "events", unique: false },
  vipXp: { home: "vip", unique: false },
};

/** One place on screen that shows a home: a selector and, where it shows a number, the element holding that number. */
export interface HomeSpot {
  readonly sel: string;
  /** Inside the spot: the number to tick up (absent: the spot only bumps). */
  readonly counter?: string;
}

const TAB = (tab: string): HomeSpot => ({ sel: `.hub .tabbar .tab[data-tab="${tab}"]` });
/** A currency on a sheet's header (components.ts currencyLineHtml / currencyPillsHtml mark it `data-cur`). */
const CUR = (cur: string): HomeSpot => ({ sel: `[data-cur="${cur}"]`, counter: ":scope > b, :scope > span" });
/** A tab of the Shop sheet's icon strip (components.ts iconTabsHtml `data-k`). */
const SHOP_TAB = (k: string): HomeSpot => ({ sel: `.k-itabs [data-k="${k}"]` });

/**
 * Each home's spots, most specific first: the counter on an open sheet that shows it, then the hub's own button.
 * targetPick.ts takes the first one that is on screen and not covered; failing that, the first on screen at all.
 */
export const HOME_SPOTS: Readonly<Record<HomeId, readonly HomeSpot[]>> = {
  gems: [CUR("gems"), { sel: '.hub .fpill[data-pill="gems"] .storebtn', counter: ":scope > b" }, TAB("shop")],
  gold: [CUR("gold"), TAB("shop")],
  marks: [CUR("marks"), TAB("shop")],
  trophies: [CUR("trophies"), SHOP_TAB("trophy"), TAB("shop")],
  armoury: [TAB("gear")],
  skins: [SHOP_TAB("skins"), TAB("shop")],
  supplies: [{ sel: ".hub .supwells" }, SHOP_TAB("supplies"), TAB("shop")],
  towers: [TAB("towers")],
  pets: [{ sel: ".hub .petchip" }, TAB("towers")],
  events: [TAB("events")],
  vip: [{ sel: '.hub .fpill[data-pill="vip"] .vipbtn' }, TAB("shop")],
  hub: [{ sel: ".hub .tabbar" }],
};

/** Where a reward flies when none of its home's spots is drawn: the hub's tab bar (dimmed or not, never off-screen). */
export const LAST_RESORT_HOME: HomeId = "hub";

/** The kinds an offer item pays (domain data/offers.ts OfferItem): a chest opens into gold and gear at once. */
export const OFFER_ITEM_PAYS: Readonly<Record<OfferItemKind, readonly RewardKind[]>> = {
  gems: ["gems"], supplies: ["gold"], speedUp: ["speedUp"], chest: ["gold", "gear"], luckyChest: ["gold", "gear"], gear: ["gear"],
  // a skin already owned pays its refund in gems
  skin: ["skin", "gems"],
};

/** The kinds each field of an event / track / pass / Trial reward pays (domain data/events.ts EventReward). */
export const EVENT_REWARD_PAYS: Readonly<Record<keyof EventReward, readonly RewardKind[]>> = {
  resources: ["gold"], speedUpMinutes: ["speedUp"], gems: ["gems"], marks: ["marks"], trophies: ["trophies"], chest: ["gold", "gear"],
  emblem: ["emblem"], essence: ["essence"], sigil: ["sigil"], sigils: ["sigil"], supply: ["supply"], supplies: ["supply"],
};

/** A Daily Order's reward (domain meta/orders.ts OrderReward). */
export const ORDER_REWARD_PAYS: Readonly<Record<keyof OrderReward, readonly RewardKind[]>> = {
  gold: ["gold"], speedUpMinutes: ["speedUp"], supply: ["supply"],
};

/** A wild pet's catch (domain meta/petCatch.ts): a level-up is progress on a pet already held, not an item, so it does not fly. */
export const CATCH_REWARD_PAYS: Readonly<Record<CatchReward["kind"], readonly RewardKind[]>> = {
  unlocked: ["pet"], levelUp: [], gold: ["gold"],
};

/** The Starfall Wyrm's reward (domain meta/wyrmReward.ts). */
export const WYRM_REWARD_PAYS: Readonly<Record<WyrmReward["kind"], readonly RewardKind[]>> = {
  skin: ["skin"], chest: ["gold", "gear", "gems"],
};

/**
 * Kinds paid outside the reward shapes above: bought outright in a shop (supplies and treats for gold or gems, skins
 * for gems or Marks), a builder hired (Deals), VIP XP from a purchase or the daily claim, embers from an event's signals.
 */
export const OTHER_SOURCES_PAY: readonly RewardKind[] = ["supply", "treat", "skin", "builder", "vipXp", "embers"];

export function isRewardKind(s: string): s is RewardKind { return (REWARD_KINDS as readonly string[]).includes(s); }
